import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ApplyDiscountToCheckoutOptions,
  ApplyDiscountToCheckoutResponse,
  LoyaltyCheckoutDiscountQuery,
  LoyaltyCheckoutDiscountsQueryBuilder,
  QueryLoyaltyCheckoutDiscountsResponse,
  applyDiscountToCheckout as universalApplyDiscountToCheckout,
  queryLoyaltyCheckoutDiscounts as universalQueryLoyaltyCheckoutDiscounts,
  typedQueryLoyaltyCheckoutDiscounts as universalTypedQueryLoyaltyCheckoutDiscounts,
} from './loyalty-checkoutexchange-v1-loyalty-checkout-discount-checkout-exchange.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/loyalty' };

export function applyDiscountToCheckout(
  httpClient: HttpClient
): ApplyDiscountToCheckoutSignature {
  return (checkoutId: string, options?: ApplyDiscountToCheckoutOptions) =>
    universalApplyDiscountToCheckout(
      checkoutId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ApplyDiscountToCheckoutSignature {
  /**
   * Applies a discount to a checkout.
   *
   * You can apply one of the following discounts, which are exchanged for loyalty points: `reward`, `loyaltyCoupon`, or `referralReward`.
   * @param - ID of the checkout to apply the discount to.
   * @returns Response after applying a discount to a checkout.
   */
  (checkoutId: string, options?: ApplyDiscountToCheckoutOptions): Promise<
    NonNullablePaths<
      ApplyDiscountToCheckoutResponse,
      | `discount.rewardId`
      | `discount.loyaltyCouponId`
      | `discount.referralRewardId`
      | `discount._id`
      | `discount.checkoutId`
      | `discount.status`
      | `discount.discountType`,
      1
    >
  >;
}

export function queryLoyaltyCheckoutDiscounts(
  httpClient: HttpClient
): QueryLoyaltyCheckoutDiscountsSignature {
  return () =>
    universalQueryLoyaltyCheckoutDiscounts(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryLoyaltyCheckoutDiscountsSignature {
  /**
   * Creates a query to retrieve a list of checkout discounts.
   *
   * The `queryLoyaltyCheckoutDiscount()` method builds a query to retrieve a list of checkout discounts and returns a `LoyaltyCheckoutDiscountQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `LoyaltyCheckoutDiscountQueryBuilder` methods onto the query. `LoyaltyCheckoutDiscountQueryBuilder` methods enable you to sort, filter, and control the results `queryLoyaltyCheckoutDiscount()` returns.
   *
   * `queryLoyaltyCheckoutDiscount()` runs with these `LoyaltyCheckoutDiscountQueryBuilder` defaults, which you can override:
   *
   * - `limit(50)`
   * - `descending("_createdDate")`
   *
   * The methods that are chained to `queryLoyaltyCheckoutDiscount()` are applied in the order they're called. For example, if you apply ascending('status') and then descending('checkoutId'), the results are sorted first by the status, and then, if there are multiple results with the same status, the items are sorted by checkout ID.
   */
  (): LoyaltyCheckoutDiscountsQueryBuilder;
}

export function typedQueryLoyaltyCheckoutDiscounts(
  httpClient: HttpClient
): TypedQueryLoyaltyCheckoutDiscountsSignature {
  return (query: LoyaltyCheckoutDiscountQuery) =>
    universalTypedQueryLoyaltyCheckoutDiscounts(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryLoyaltyCheckoutDiscountsSignature {
  /** @returns Response from querying loyalty checkout discounts. */
  (query: LoyaltyCheckoutDiscountQuery): Promise<
    NonNullablePaths<
      QueryLoyaltyCheckoutDiscountsResponse,
      | `loyaltyCheckoutDiscounts`
      | `loyaltyCheckoutDiscounts.${number}.rewardId`
      | `loyaltyCheckoutDiscounts.${number}.loyaltyCouponId`
      | `loyaltyCheckoutDiscounts.${number}.referralRewardId`
      | `loyaltyCheckoutDiscounts.${number}._id`
      | `loyaltyCheckoutDiscounts.${number}.checkoutId`
      | `loyaltyCheckoutDiscounts.${number}.status`
      | `loyaltyCheckoutDiscounts.${number}.discountType`,
      2
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddressLocation,
  ApplyDiscountToCheckoutOptions,
  ApplyDiscountToCheckoutOptionsDiscountOneOf,
  ApplyDiscountToCheckoutRequest,
  ApplyDiscountToCheckoutRequestDiscountOneOf,
  ApplyDiscountToCheckoutResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DiscountRefundedDueToManualDiscountRemoval,
  DiscountRefundedDueToTimeout,
  DiscountScheduledForRefund,
  DiscountStatus,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  IdentificationData,
  IdentificationDataIdOneOf,
  LoyaltyCheckoutDiscount,
  LoyaltyCheckoutDiscountDiscountOneOf,
  LoyaltyCheckoutDiscountQuerySpec,
  LoyaltyCheckoutDiscountsQueryBuilder,
  LoyaltyCheckoutDiscountsQueryResult,
  LoyaltyCoupon,
  MessageEnvelope,
  QueryLoyaltyCheckoutDiscountsRequest,
  QueryLoyaltyCheckoutDiscountsResponse,
  ReferralReward,
  RestoreInfo,
  Reward,
  SortOrder,
  Sorting,
  WebhookIdentityType,
  utils,
} from './loyalty-checkoutexchange-v1-loyalty-checkout-discount-checkout-exchange.universal.js';
